import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import { Container, Draggable } from "react-smooth-dnd";
import { arrayMoveImmutable } from "array-move";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import SweetAlert from "sweetalert2";
import { toast } from "react-toastify";
import PreLoader from "../../../../share-components/ui/preLoader";
import QuestionAnswerFormModal from "./questionAnswerFormModal";
import { getQaCategoryApi } from "../../../../services/manage-product/setting/qaCategoryService";
import {
  getProductQuestionsAnswersApi,
  addProductQuestionAnswerApi,
  updateProductQuestionAnswerApi,
  deleteProductQuestionAnswerApi,
  reorderProductQuestionsAnswersApi,
} from "../../../../services/manage-product/productService";

const sortByPosition = (rows) =>
  [...rows].sort((a, b) => (a.display_order - b.display_order) || (a.id - b.id));

// The shared axios interceptor (src/utils/api.jsx) already shows a toast itself for 422
// and 401 responses (and rejects with the raw response body, not an Error, in those two
// cases) — only re-toast here when the rejection is a real AxiosError that wasn't already
// surfaced globally, so mutation failures aren't double-toasted. Same convention as
// manageWearItToModal.jsx / manageFabricCareModal.jsx.
const notifyMutationError = (err, fallback) => {
  if (err && err.isAxiosError && err.response?.status !== 404) {
    toast.error(err.response?.data?.message || err.message || fallback);
  }
};

/**
 * List-managing modal for a product's Question & Answer entries, grouped into the same
 * categories shown as tabs on the storefront (e.g. "Fabric & Care", "Fit & Sizing") —
 * each category is its own independently reorderable section, since display_order only
 * has meaning within a category (see ProductQuestionAnswerController::reorder). Every
 * category from the global qa_categories library is shown, even with zero entries yet,
 * so staff can add the first Q&A into any category without leaving this modal.
 */
const ManageQuestionsAnswersModal = ({ open, onClose, productId, productCode, productColour }) => {
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [mutating, setMutating] = useState(false);
  const [reorderingCategoryId, setReorderingCategoryId] = useState(null);

  // A reorder persist and any other mutation must never overlap — both would race to write
  // display_order for the same scoped product/category, so one combined flag gates both
  // drag and every other Add/Edit/Delete control.
  const busy = mutating || reorderingCategoryId !== null;

  // null | "create" | "edit"
  const [formMode, setFormMode] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  // Which category a new entry is being added into — irrelevant (and unused) in edit mode,
  // where the record's own qa_category_id never changes.
  const [targetCategoryId, setTargetCategoryId] = useState(null);

  const fetchAll = (silent = false) => {
    if (!productId) return;
    if (!silent) {
      setLoading(true);
      setLoadError("");
    }
    Promise.all([getQaCategoryApi(), getProductQuestionsAnswersApi(productId)])
      .then(([categoryRes, entryRes]) => {
        setCategories(Array.isArray(categoryRes.response) ? categoryRes.response : []);
        setEntries(sortByPosition(Array.isArray(entryRes.response) ? entryRes.response : []));
      })
      .catch((err) => {
        // A silent (post-mutation) reconcile failing shouldn't blow away the optimistic
        // state the user already sees — only surface the retry UI for the initial load.
        if (!silent) {
          setLoadError(err?.response?.data?.message || err?.message || "Failed to load Q&A entries.");
        }
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  useEffect(() => {
    // Fully clear stale entries/product scope so a later reopen never flashes a previous
    // product's rows, whether we're closing or switching to a different productId.
    setCategories([]);
    setEntries([]);
    setLoadError("");
    setLoading(false);
    setFormMode(null);
    setEditingRecord(null);
    setTargetCategoryId(null);

    if (open && productId) {
      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId]);

  // Entries grouped by category, in each category's own display_order — computed fresh
  // from the flat `entries` state rather than stored separately, so there's only ever one
  // source of truth to keep in sync after a mutation.
  const entriesByCategory = useMemo(() => {
    const map = {};
    entries.forEach((entry) => {
      const key = entry.qa_category_id;
      if (!map[key]) map[key] = [];
      map[key].push(entry);
    });
    Object.keys(map).forEach((key) => {
      map[key] = sortByPosition(map[key]);
    });
    return map;
  }, [entries]);

  const openAddForm = (categoryId) => {
    setEditingRecord(null);
    setTargetCategoryId(categoryId);
    setFormMode("create");
  };

  const openEditForm = (row) => {
    setEditingRecord(row);
    setTargetCategoryId(row.qa_category_id);
    setFormMode("edit");
  };

  const closeForm = () => {
    setFormMode(null);
    setEditingRecord(null);
    setTargetCategoryId(null);
  };

  // Passed to QuestionAnswerFormModal as `onSubmit`. Must reject (not swallow) on failure
  // so the form modal itself stays open and renders the field-level errors — only a
  // resolved promise here closes the form and refreshes the list.
  const handleFormSubmit = (values) => {
    if (formMode === "edit" && editingRecord) {
      // Category and position are left untouched on a plain edit — only dragging
      // (handleReorderDrop) changes display_order, and category is fixed at creation.
      return updateProductQuestionAnswerApi(editingRecord.id, productId, {
        ...values,
        qa_category_id: editingRecord.qa_category_id,
        display_order: editingRecord.display_order,
      }).then(({ response }) => {
        setEntries((prev) => sortByPosition(prev.map((e) => (e.id === editingRecord.id ? response : e))));
        toast.success("Question & answer updated.");
        closeForm();
        fetchAll(true);
      });
    }

    const categoryEntries = entriesByCategory[targetCategoryId] || [];
    const nextPosition = categoryEntries.length ? Math.max(...categoryEntries.map((e) => e.display_order)) + 1 : 0;
    return addProductQuestionAnswerApi(productId, {
      ...values,
      qa_category_id: targetCategoryId,
      display_order: nextPosition,
    }).then(({ response }) => {
      setEntries((prev) => sortByPosition([...prev, response]));
      toast.success("Question & answer added.");
      closeForm();
      fetchAll(true);
    });
  };

  const handleDelete = (row) => {
    SweetAlert.fire({
      title: "Delete this question & answer?",
      html: `This will permanently remove <b>${row.question}</b>.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) return;

      setMutating(true);
      deleteProductQuestionAnswerApi(row.id)
        .then(() => {
          setEntries((prev) => prev.filter((e) => e.id !== row.id));
          toast.success("Question & answer deleted.");
        })
        .catch((err) => {
          if (err?.isAxiosError && err.response?.status === 404) {
            // Already removed (e.g. by another editor) — drop it locally, no error toast.
            setEntries((prev) => prev.filter((e) => e.id !== row.id));
            return;
          }
          notifyMutationError(err, "Failed to delete the entry.");
        })
        .finally(() => setMutating(false));
    });
  };

  const handleReorderDrop = (categoryId, { removedIndex, addedIndex }) => {
    // Guard even though the handle is visually/pointer-disabled while busy — belt and
    // braces against any stray drop event, and against no-op drops.
    if (busy || removedIndex === null || addedIndex === null || removedIndex === addedIndex) return;

    const categoryEntries = entriesByCategory[categoryId] || [];
    const reordered = arrayMoveImmutable(categoryEntries, removedIndex, addedIndex).map((row, index) => ({
      ...row,
      display_order: index,
    }));

    const lastKnownGoodEntries = entries;
    // Splice the reordered category's rows back into the flat state, leaving every other
    // category's rows untouched.
    setEntries((prev) => sortByPosition([...prev.filter((e) => e.qa_category_id !== categoryId), ...reordered]));
    setReorderingCategoryId(categoryId);

    const items = reordered.map((row) => ({ id: row.id, display_order: row.display_order }));

    reorderProductQuestionsAnswersApi(productId, categoryId, items)
      .then(({ response }) => {
        // The server's persisted rows are the new source of truth (survives close/reopen
        // and reload on their own merit — this just keeps the in-memory view in sync now).
        const serverRows = Array.isArray(response) ? response : reordered;
        setEntries((prev) => sortByPosition([...prev.filter((e) => e.qa_category_id !== categoryId), ...serverRows]));
      })
      .catch((err) => {
        setEntries(lastKnownGoodEntries);
        notifyMutationError(err, "Failed to save the new order.");
      })
      .finally(() => setReorderingCategoryId(null));
  };

  return (
    <>
      <Modal isOpen={open} toggle={onClose} className="modal-dialog-centered" size="lg">
        <ModalHeader toggle={onClose} tag="div">
          <div>Manage Questions & Answers</div>
          {(productCode || (Array.isArray(productColour) && productColour.filter(Boolean).length > 0)) && (
            <div style={{ fontSize: "0.8rem", fontWeight: 400, color: "#888", marginTop: 2 }}>
              {[productCode, Array.isArray(productColour) ? productColour.filter(Boolean).join(", ") : productColour]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
        </ModalHeader>
        <ModalBody style={{ minHeight: 200, position: "relative", maxHeight: "70vh", overflowY: "auto" }}>
          {loading && <PreLoader withoutText />}

          {!loading && loadError && (
            <div className="alert alert-danger">
              <div className="font-weight-bold mb-1">Q&A entries could not be loaded</div>
              <div className="mb-2">{loadError}</div>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => fetchAll()}>
                Retry
              </button>
            </div>
          )}

          {!loading && !loadError && categories.length === 0 && (
            <div className="text-muted">
              No Q&A categories configured yet. Add some under Product Setting → Q&A Categories.
            </div>
          )}

          {!loading && !loadError && categories.length > 0 && (
            <>
              {categories.map((category) => {
                const categoryEntries = entriesByCategory[category.id] || [];
                const categoryBusy = busy && reorderingCategoryId === category.id;
                return (
                  <div key={category.id} className="mb-4">
                    <div
                      className="text-uppercase font-weight-bold mb-2"
                      style={{ fontSize: "0.78rem", letterSpacing: "0.04em", color: "#555" }}
                    >
                      {category.title}
                    </div>

                    {categoryEntries.length > 0 ? (
                      <div style={{ marginBottom: 12 }}>
                        <Container
                          dragHandleSelector=".drag-handle"
                          lockAxis="y"
                          onDrop={(dropResult) => handleReorderDrop(category.id, dropResult)}
                        >
                          {categoryEntries.map((row) => (
                            <Draggable key={row.id}>
                              <div className="d-flex align-items-start border-bottom py-2" style={{ background: "#fff" }}>
                                <span
                                  className="drag-handle"
                                  style={{
                                    cursor: busy ? "not-allowed" : "grab",
                                    pointerEvents: busy ? "none" : "auto",
                                    opacity: busy ? 0.4 : 1,
                                    marginRight: 8,
                                    marginTop: 2,
                                    flexShrink: 0,
                                  }}
                                >
                                  <DragHandleIcon />
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontWeight: 600,
                                      wordBreak: "break-word",
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {row.question}
                                  </div>
                                  <div
                                    className="text-muted"
                                    style={{
                                      fontSize: "0.85rem",
                                      wordBreak: "break-word",
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {row.answer}
                                  </div>
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: 12, marginLeft: 12, flexShrink: 0 }}>
                                  <Button color="link" className="p-0" disabled={busy} onClick={() => openEditForm(row)}>
                                    <i className="icon-pencil" />
                                  </Button>
                                  <Button color="link" className="p-0 text-danger" disabled={busy} onClick={() => handleDelete(row)}>
                                    <i className="icon-trash" />
                                  </Button>
                                </div>
                              </div>
                            </Draggable>
                          ))}
                        </Container>
                        {categoryBusy && <small className="text-muted">Saving new order…</small>}
                      </div>
                    ) : (
                      <div className="text-muted mb-2" style={{ fontSize: "0.9rem" }}>
                        No Q&A entries yet in this category.
                      </div>
                    )}

                    <Button color="link" className="p-0" disabled={busy} onClick={() => openAddForm(category.id)}>
                      + Add Question & Answer
                    </Button>
                  </div>
                );
              })}
            </>
          )}
        </ModalBody>
      </Modal>

      <QuestionAnswerFormModal
        open={Boolean(formMode)}
        onClose={closeForm}
        mode={formMode || "create"}
        record={editingRecord}
        onSubmit={handleFormSubmit}
      />
    </>
  );
};

export default ManageQuestionsAnswersModal;
